/**
 * Shared Menu Chrome
 *
 * The title screen's visual language, extracted so every menu screen (Arena,
 * Tournament, Leaderboard) is built from the same parts instead of inventing
 * its own: the original 2001 art's white double-rimmed button (`.dw-btn`,
 * after BT_GRAPH: #fff face, #ccc inner edge, #333 rim) and bare Anton text
 * option (`.dw-opt`), the wordmark-style bevel headline
 * (`.dw-screen-title`, set in the logotype's own layer palette), the Roboto
 * eyebrow label, and the translucent panel that carries dense data over the
 * live attract-mode board. All of it floats on `var(--ui-scrim)` — the same
 * tint the title screen uses over the background game.
 *
 * Also home to the mode rail (`TopNav`): the tab bar App mounts across the
 * hub screens (NAV_TABS — Battle, Arena, Tournament, Leaderboard). It replaced
 * the per-screen BACK buttons, so on Arena / Tournament / Leaderboard it is
 * the only way around (and back to Battle); the current tab is set in the
 * logotype bevel at miniature scale, so "where you are" is always written in
 * the game's own lettering. The title screen itself does NOT carry the rail:
 * playtesting found it gave the bot-author screens the same weight as
 * starting a game (#182), so there the same destinations sit in a footer link
 * row (`FooterNav`) at the foot of the page, and the landing page's scan path
 * is setup → START.
 *
 * The white button keeps fixed colors across themes: like the wordmark itself,
 * it's part of the game's identity and reads well over the scrimmed board in
 * both dark and light. The headline and active-tab bevels are identity too,
 * but they could not stay fixed — the wordmark's orange is unreadable on the
 * light theme's pale scrim (#220) — so they go through the `--ui-bevel-*`
 * tokens, which hold the wordmark's own palette on the dark theme and a
 * darkened cast of it on the light one. Everything else theme-dependent goes
 * through var(--ui-*).
 *
 * @module ui/menuChrome
 */

import { Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

/*
 * Interactive states (hover/active/focus/disabled) can't be done with inline
 * styles, so the shared classes live in this stylesheet. Everything that uses
 * the classes mounts its own copy — MenuScreen, TitleScreen, MapPreview,
 * SettingsPanel, GameHUD, QuitConfirm — so each is self-contained instead of
 * relying on some other component happening to be on screen. Several are
 * therefore mounted at once (SettingsPanel rides along on every screen, and the
 * quit confirm sits on top of the HUD), which is harmless: the rules are
 * identical, so whichever copy wins declares the same thing. Three rules do
 * override one of these — SettingsPanel's `.dw-opt.dw-set-opt` padding, the
 * in-game bar's `.dw-opt.dw-hud-opt` sizing (#222 item 1), and RulesModal's
 * `.dw-opt.dw-rules-close`. Every screen that overrides a rule in here mounts
 * this sheet concatenated with its own inside a single <style> (SettingsPanel
 * and RulesModal do; so do QuitConfirm and TitleScreen, which override nothing)
 * — so such an override is later text in the same sheet rather than a rule in
 * another one, and would tie with the rule it means to beat instead of losing
 * to it. Each therefore doubles its class to win on specificity rather than
 * source order, so mount order never decides anything. The coarse-pointer block
 * at the foot of this sheet is where all three are listed and explained.
 */
export const CHROME_CSS = `
.dw-btn {
  font-family: Anton, sans-serif;
  color: #111111;
  background: #ffffff;
  border: 3px solid #333333;
  border-radius: 12px;
  box-shadow: inset 0 0 0 3px #cccccc, 0 4px 0 rgba(0, 0, 0, 0.3);
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: transform 0.08s ease, box-shadow 0.08s ease, border-color 0.12s ease;
}
.dw-btn:hover { border-color: #7a7a7a; }
.dw-btn:active {
  transform: translateY(3px);
  box-shadow: inset 0 0 0 3px #cccccc, 0 1px 0 rgba(0, 0, 0, 0.3);
}
.dw-btn:focus-visible { outline: 3px solid var(--ui-accent); outline-offset: 3px; }
.dw-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: inset 0 0 0 3px #cccccc, 0 4px 0 rgba(0, 0, 0, 0.3);
}
a.dw-btn { display: inline-block; text-decoration: none; }

.dw-opt {
  font-family: Anton, sans-serif;
  background: transparent;
  border: none;
  padding: 0.1rem 0.4rem;
  color: var(--ui-text-muted);
  text-shadow: var(--ui-text-halo);
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: color 0.12s ease;
}
.dw-opt:hover { color: var(--ui-text); }
.dw-opt[aria-pressed='true'] { color: var(--ui-accent); }
.dw-opt:focus-visible {
  outline: 2px solid var(--ui-accent);
  outline-offset: 2px;
  border-radius: 4px;
}
.dw-opt:disabled { opacity: 0.5; cursor: not-allowed; }

/*
 * Screen headline in the DICE WARS logotype's own bevel: rim light up-left,
 * amber face, and the wordmark's layer stack extruded down-right over a soft
 * drop shadow like the one TitleScreen puts under the wordmark. The stack is a
 * theme token (see themes.js, uiBevel*) rather than the wordmark's literal
 * palette, because the display face has to darken on the light theme to clear
 * 3:1 against the pale scrim; on the dark theme it still resolves to
 * titleArt.jsx's exact values.
 */
.dw-screen-title {
  font-family: Anton, sans-serif;
  font-size: clamp(2.3rem, 6vw, 3rem);
  letter-spacing: 0.08em;
  color: var(--ui-bevel-face-display);
  text-shadow: var(--ui-bevel-shadow-display);
}

@keyframes dw-rise {
  from { opacity: 0; transform: translateY(-14px); }
  to { opacity: 1; transform: none; }
}
@keyframes dw-pop {
  0% { opacity: 0; transform: scale(0.82) rotate(-3deg); }
  70% { transform: scale(1.04) rotate(0.5deg); }
  100% { opacity: 1; transform: none; }
}
@keyframes dw-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.dw-anim-rise { animation: dw-rise 0.4s ease-out both; }
.dw-anim-pop { animation: dw-pop 0.45s ease-out 0.1s both; }
.dw-anim-fade { animation: dw-fade 0.35s ease-out 0.2s both; }
@media (prefers-reduced-motion: reduce) {
  .dw-anim-rise, .dw-anim-pop, .dw-anim-fade { animation: none; }
}

/*
 * Touch: a hit area bigger than the glyphs. The bare-text option is the game's
 * own idiom and the type is untouched — what the 2026-08-31 audit measured
 * (#222) is the box around it. Option rows came out 28-33px tall and the
 * leaderboard's compact watch button 59x28, against the 24px floor WCAG 2.5.8
 * sets and the 44pt Apple's guidelines ask for (Material asks for 48dp). 40px
 * is what the option rows can carry: a wrapping row of seven player counts
 * turns into a page of its own well before 44.
 *
 * Coarse pointers only, so the mouse layout is byte for byte what it was.
 * (pointer: coarse) asks about the finger, not the viewport, so a narrow
 * desktop window keeps the tight rows and a tablet gets the roomy ones.
 *
 * This block is LAST in the sheet on purpose: its .dw-opt is the same
 * specificity as the base rule above, so source order is the whole reason it
 * wins (pinned in tests/ui/touchTargets.test.js), and the a.dw-btn rule below
 * ties with its own base rule the same way. The four places that must NOT be
 * reached opt out the other way, on specificity, by doubling their class — so
 * none of them depends on which copy of this sheet a screen happens to mount,
 * nor on which other sheet a screen concatenates onto it:
 *
 *   .dw-opt.dw-set-opt (the settings dropdown) — its horizontal padding is
 *   cancelled by a matching negative margin on .dw-set-row, so widening it
 *   would pull the option text out of eyebrow alignment; that sheet grows its
 *   own rows vertically instead.
 *
 *   .dw-opt.dw-hud-opt (the in-game bar) — the bar's height is a contract with
 *   the renderer, published by the bar itself as the --dw-hud-bar-height
 *   custom property (HUD_BAR_HEIGHT's 50px on a desktop, two stacked rows and
 *   about 80px under 560px), so its two text controls have to grow into the
 *   bar rather than push it open, which they do with a layout-neutral
 *   negative-margin rule of their own (#222 item 1).
 *
 *   .dw-opt.dw-rules-close (the how-to-play card's corner close button) —
 *   RulesModal mounts this sheet concatenated with its own inside one <style>,
 *   the way every screen that overrides a rule in here does, so a rule there is
 *   later text in the same sheet rather than a rule in another one, and a bare
 *   class would tie with this block instead of losing to it. It keeps its tight
 *   padding on purpose and takes its 40px box from min-width / min-height
 *   instead.
 *
 *   .dw-opt.dw-daily-opt (the title screen's daily card button) — the card
 *   mounts no copy of this sheet at all, so its option button is styled purely
 *   by whichever copy another component happens to have mounted; doubling the
 *   class is what makes its own box independent of that. It is a single wide
 *   button rather than a row of them, so it can afford a roomier target than
 *   the generic rule gives, and takes 44px on a coarse pointer.
 *
 * .dw-btn only needs the floor: the filled buttons already clear 40px, so the
 * rule is a floor rather than a resize, and two controls are short enough today
 * for it to show — the leaderboard's compact watch button and the link out to
 * the bot guide on the arena and tournament screens, both of which set their
 * padding inline. That link is an anchor, and an inline-block anchor does not
 * center its label in a box the floor has grown the way a button does, so the
 * anchor takes flex centering here as well.
 *
 * Comments in here are rendered text: every sheet ships inside a <style> the
 * component mounts, so an all-caps interface word written in a comment lands
 * in that element's textContent. Keep the prose lowercase.
 */
@media (pointer: coarse) {
  .dw-opt { padding: 0.45rem 0.6rem; min-height: 40px; }
  .dw-btn { min-height: 40px; }
  a.dw-btn { display: inline-flex; align-items: center; justify-content: center; }
}
`;

/** Shared inline-style fragments for menu screens. */
export const MENU_STYLE = {
  /**
   * Full-viewport scroll column over the scrimmed live board. The top padding
   * clears the fixed mode rail (~55px) plus breathing room.
   */
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    overflowY: 'auto',
    padding: '4.6rem 1rem 2rem',
    background: 'var(--ui-scrim)',
    pointerEvents: 'auto',
    userSelect: 'none',
    color: 'var(--ui-text)',
  },
  screenTitle: {
    margin: '0 0 1.3rem',
    textAlign: 'center',
  },
  /** One centered content block (an option group, a panel, results). */
  section: {
    width: '100%',
    maxWidth: '560px',
    marginBottom: '1.1rem',
    textAlign: 'center',
  },
  /** Roboto meta label above an option row — the title's "MAP SIZE" idiom. */
  eyebrow: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.68rem',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
    textShadow: 'var(--ui-text-halo)',
    marginBottom: '0.2rem',
  },
  /** Small helper line under an option group. */
  caption: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    textShadow: 'var(--ui-text-halo)',
    marginTop: '0.35rem',
  },
  /** Wrapping row of .dw-opt toggles. */
  optRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: '0.15rem',
  },
  buttonRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    margin: '0.5rem 0 1.4rem',
  },
  primaryBtn: {
    fontSize: '1.25rem',
    padding: '0.6rem 2.2rem',
  },
  secondaryBtn: {
    fontSize: '0.95rem',
    padding: '0.55rem 1.2rem',
  },
  /*
   * The biggest button in the game and its smaller sibling: the title's START
   * and the map preview's PLAY / NEW MAP share these so the two screens read as
   * one flow (START → PLAY) instead of drifting apart. The title's AI vs AI
   * used to be the second one; since #182 it is a bare .dw-opt text link so
   * START is the landing page's single filled control.
   */
  heroBtn: {
    fontSize: 'clamp(1.35rem, 3vw, 1.6rem)',
    padding: '0.7rem 2.8rem',
  },
  heroSecondaryBtn: {
    fontSize: '1.05rem',
    padding: '0.6rem 1.3rem',
  },
  /** Translucent card for dense data — the title's customizePanel idiom. */
  panel: {
    width: '100%',
    padding: '0.9rem 1rem',
    background: 'var(--ui-overlay-bg)',
    border: '1px solid var(--ui-border)',
    borderRadius: '10px',
  },
  statsRow: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.85rem',
    color: 'var(--ui-text-muted)',
    textShadow: 'var(--ui-text-halo)',
    marginBottom: '0.8rem',
    textAlign: 'center',
  },
  errorBanner: {
    background: 'var(--ui-accent-soft)',
    border: '1px solid var(--ui-accent)',
    color: 'var(--ui-accent)',
    padding: '0.6rem 1.2rem',
    borderRadius: '6px',
    margin: '0 0 1.2rem',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.95rem',
    maxWidth: '440px',
    textAlign: 'center',
  },
};

/**
 * Scrimmed container + bevel headline shared by every menu screen. Children
 * flow as centered sections below the title.
 *
 * @param {Object} props
 * @param {string} props.title - Headline text (rendered in the wordmark bevel)
 * @param {import('preact').ComponentChildren} props.children
 * @param {boolean} [props.focusTitleOnMount] - Take focus onto the headline
 *   when the screen mounts. Opt-in; see the effect below for why it is off.
 */
export function MenuScreen({ title, children, focusTitleOnMount = false }) {
  const titleRef = useRef(null);

  /*
   * The route-change focus convention's generic fallback: a screen arrived at
   * by activating a control that unmounted with the previous screen has to put
   * focus somewhere, and a screen with no obvious primary control can land it
   * on the headline instead — which also announces the destination.
   *
   * Opt-in, and off by default, because no current consumer wants it: Arena,
   * Tournament and OnlineLeaderboard are all rail screens, and TopNav already
   * focuses its aria-current tab on mount (#188). App renders the rail as an
   * EARLIER sibling than the screen content, so an unconditional headline focus
   * here would run after TopNav's effect and steal focus straight back off the
   * tab — a #188 regression. Leave it off for anything with a rail.
   *
   * tabIndex={-1} is unconditional: it makes the h1 focusable programmatically
   * without putting it in the tab order.
   *
   * What a focus here would LOOK like is only half-settled (#211 item 6). No
   * author rule reaches the headline: every :focus-visible rule in the shared
   * chrome styles is scoped to a chrome class (.dw-btn, .dw-opt, .dw-tab,
   * .dw-footlink), and .dw-screen-title / .dw-anim-rise, the two the h1 does
   * carry, have no focus rule of their own. But that is not the same as no ring:
   * every engine's UA stylesheet carries a universal `:focus-visible { outline:
   * auto }`, and nothing in index.html or src/ resets outlines, so if the
   * headline matches the pseudo-class it rings from there. And whether a
   * mount-time scripted focus() matches is an engine heuristic, not a rule to
   * rely on: the spec has a scripted focus inherit the previously focused
   * element's state (at mount that element is gone — `<body>`), while Chromium
   * and Firefox match when the last user interaction was a keypress, which is
   * exactly what a keyboard-activated route change is.
   *
   * Untested in any browser, because nothing in production passes the flag —
   * only a TopNav unit test does. A screen that opts in should look at it for
   * real and then either accept the ring (a focus landing on a headline is
   * allowed to show) or add `outline: none` to a `.dw-screen-title:focus-visible`
   * rule in CHROME_CSS.
   *
   * Its one side effect is silent: Chrome and Safari focus a tabindex="-1"
   * element on mousedown, so a click on the headline moves focus there (still
   * no ring) and the next Tab picks up from the screen's first control instead
   * of wherever focus was.
   */
  useEffect(() => {
    if (focusTitleOnMount) titleRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div style={MENU_STYLE.container}>
      <style>{CHROME_CSS}</style>
      <h1
        className="dw-screen-title dw-anim-rise"
        style={MENU_STYLE.screenTitle}
        ref={titleRef}
        tabIndex={-1}
      >
        {title}
      </h1>
      {children}
    </div>
  );
}

/**
 * Canonical URL of the project's source repository. Single source of truth for
 * every in-app GitHub link (the title screen's footer, the settings panel's
 * footer, and AddBotViaGithub's deep link into docs/BOT_GUIDE.md), so the repo
 * can move without hunting down hardcoded URLs.
 */
export const REPO_URL = 'https://github.com/bigintersmind/dicewarsjs';

/**
 * The hub screens, in rail order. `id` values are `store.screen` names; this
 * list is also how App decides where the hub chrome shows — the rail on every
 * hub screen but the title, FooterNav on the title — and so must stay in sync
 * with ATTRACT_SCREENS in TitleAttractMode.js (the chrome belongs exactly
 * where the live attract board runs behind it).
 */
export const NAV_TABS = [
  { id: 'title', label: 'Battle' },
  { id: 'arena', label: 'Arena' },
  { id: 'tournament', label: 'Tournament' },
  { id: 'onlineLeaderboard', label: 'Leaderboard' },
];

/*
 * Self-contained (not part of CHROME_CSS): the rail is mounted by App outside
 * the screen switch, so it must stay styled while a lazy screen chunk is still
 * loading and no screen stylesheet is mounted. The rail band adds a second
 * layer of --ui-scrim on top of the screen's own, reading as a slightly deeper
 * strip of the same tint. The active tab is CSS-only: `aria-current="page"`
 * carries both the semantics and the bevel (declared after :hover so the bevel
 * wins on an active tab). The bevel colors come from the `--ui-bevel-*`
 * tokens: titleArt.jsx's wordmark palette on the dark theme, a darkened cast
 * of it on the light one, where the orange face misses 4.5:1 over the pale
 * scrim (#220).
 */
const NAV_CSS = `
.dw-topnav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 900;
  display: flex;
  overflow-x: auto;
  background: var(--ui-scrim);
  border-bottom: 1px solid var(--ui-border);
  pointer-events: auto;
  user-select: none;
}
/* margin:auto centers the rail but still yields to the scroll edge when the
   tabs outgrow a narrow viewport (a flex 'safe center' that works everywhere).
   The side padding keeps the last tab clear of the settings die. */
.dw-topnav-rail {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  margin: 0 auto;
  padding: 0 3.4rem;
}
.dw-tab {
  font-family: Anton, sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border: none;
  padding: 1rem 0.7rem;
  color: var(--ui-text-muted);
  text-shadow: var(--ui-text-halo);
  cursor: pointer;
  transition: color 0.12s ease;
  white-space: nowrap;
}
.dw-tab:hover { color: var(--ui-text); }
.dw-tab:focus-visible {
  outline: 2px solid var(--ui-accent);
  outline-offset: -2px;
  border-radius: 4px;
}
/* The logotype bevel, miniaturized. No rim light at this scale — a 15px
   glyph's strokes are as thin as the rim, which turns it to mush; the amber
   face + tight extrusion is the smallest treatment that still reads as the
   wordmark. At 15px the face owes a full 4.5:1, which the wordmark's orange
   misses badly over the light scrim (1.9:1) — the extrusion under it adds edge
   contrast but WCAG measures glyph against ground — so the face comes from
   --ui-bevel-face, which the light theme darkens into the same amber family
   (5.0:1) and the dark theme leaves at the wordmark's own orange. */
.dw-tab[aria-current='page'] {
  color: var(--ui-bevel-face);
  text-shadow: var(--ui-bevel-shadow);
  cursor: default;
}
@keyframes dw-nav-drop {
  from { opacity: 0; transform: translateY(-100%); }
  to { opacity: 1; transform: none; }
}
.dw-topnav-drop { animation: dw-nav-drop 0.4s ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .dw-topnav-drop { animation: none; }
}
/* Phones: tighten until all four tabs fit beside the settings die at 360px.
   Only the right side needs die-button clearance. */
@media (max-width: 560px) {
  .dw-tab { font-size: 0.78rem; padding: 1rem 0.35rem; }
  .dw-topnav-rail { gap: 0.15rem; padding: 0 3rem 0 0.75rem; }
}
`;

/**
 * Mode rail across the hub screens other than the title (which carries
 * FooterNav instead, #182). Pure: the current screen comes in as a prop and
 * taps report the target screen id back through onNavigate — App owns the
 * actual controller navigation.
 *
 * @param {Object} props
 * @param {string} props.active - `store.screen` id of the current hub screen
 * @param {(screenId: string) => void} props.onNavigate - Called with the
 *   tapped tab's screen id (never the active one)
 * @param {boolean} [props.animate] - Play the one-time drop-in entrance
 *   (disabled for the in-app reduced-motion preference; the system-level
 *   preference is handled in CSS)
 */
export function TopNav({ active, onNavigate, animate = true }) {
  /*
   * Take focus onto the current tab when the rail mounts. Since #182 the title
   * has no rail, so activating a footer link unmounts the button the user was
   * on and focus drops to <body> — the rail is what mounts on that transition,
   * so it picks focus back up, and landing on the aria-current tab also
   * announces the destination to a screen reader. Mount only: rail-to-rail
   * navigation never remounts TopNav, so focus stays wherever the user put it.
   */
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <nav className={animate ? 'dw-topnav dw-topnav-drop' : 'dw-topnav'} aria-label="Game screens">
      <style>{NAV_CSS}</style>
      <div className="dw-topnav-rail">
        {NAV_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className="dw-tab"
            ref={tab.id === active ? activeRef : undefined}
            aria-current={tab.id === active ? 'page' : undefined}
            onClick={tab.id === active ? undefined : () => onNavigate(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

/*
 * Self-contained like NAV_CSS: the footer row is the title screen's whole
 * footer and must read the same whether or not any other chrome stylesheet
 * is mounted. Anton, not Roboto, on purpose — the title's rule is Anton for
 * in-game actions (option text, START, tabs) and Roboto for meta text
 * (eyebrows, captions, links out) — but small, muted and unbevelled, so it
 * reads as the way to the bot screens rather than as a peer of the game setup.
 */
const FOOTER_NAV_CSS = `
.dw-footnav {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  pointer-events: auto;
  user-select: none;
}
.dw-footlink {
  font-family: Anton, sans-serif;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border: none;
  padding: 0.15rem 0.45rem;
  color: var(--ui-text-muted);
  text-shadow: var(--ui-text-halo);
  cursor: pointer;
  transition: color 0.12s ease;
  white-space: nowrap;
}
.dw-footlink:hover { color: var(--ui-text); }
.dw-footlink:focus-visible {
  outline: 2px solid var(--ui-accent);
  outline-offset: 2px;
  border-radius: 4px;
}
.dw-footnav-sep {
  font-size: 0.85rem;
  color: var(--ui-text-muted);
  text-shadow: var(--ui-text-halo);
}

/*
 * Touch, same reasoning as CHROME_CSS's coarse block: the audit measured these
 * links 25px tall (#222) — a hair over the 24px floor and nowhere near the
 * 40px a finger wants, on a row that wraps on a phone so the taps land close
 * together. The row is centered and baseline-aligned, so the extra padding
 * only spaces the three links apart and drops the row's baseline; the
 * separators follow it. Nothing moves on a mouse.
 */
@media (pointer: coarse) {
  .dw-footlink { padding: 0.5rem 0.6rem; min-height: 40px; }
}
`;

/**
 * The title screen's footer link row — Arena · Tournament · Leaderboard, i.e.
 * NAV_TABS minus the title's own Battle tab — the row only mounts on the
 * title. Same contract as TopNav: taps report the target screen id through
 * onNavigate and App owns the navigation. Placed at the foot of the page
 * (#182): these are the bot-author screens, footer material, and the rail on
 * each of them is the way back to Battle.
 *
 * @param {Object} props
 * @param {(screenId: string) => void} props.onNavigate - Called with the
 *   tapped link's screen id
 */
export function FooterNav({ onNavigate }) {
  const links = NAV_TABS.filter(tab => tab.id !== 'title');
  return (
    <nav className="dw-footnav" aria-label="More game modes">
      <style>{FOOTER_NAV_CSS}</style>
      {links.map((tab, i) => (
        <Fragment key={tab.id}>
          {i > 0 && (
            <span className="dw-footnav-sep" aria-hidden="true">
              &middot;
            </span>
          )}
          <button type="button" className="dw-footlink" onClick={() => onNavigate(tab.id)}>
            {tab.label}
          </button>
        </Fragment>
      ))}
    </nav>
  );
}
